import React, { useState, useEffect } from 'react';
import Card from '../common/Card';
import Table from '../common/Table';
import Button from '../common/Button';
import Modal from '../common/Modal';
import Input from '../common/Input';
import api from '../../services/api';
import { formatDate } from '../../utils/formatters';
import '../../styles/admin.css';

const SystemUsers = () => {
  const [users, setUsers] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [usersRes, businessesRes, rolesRes] = await Promise.all([
        api.get('/admin/users'),
        api.get('/admin/businesses?limit=100'),
        api.get('/admin/roles')
      ]);
      
      setUsers(usersRes.data.users || []);
      setBusinesses(businessesRes.data.businesses || []);
      setRoles(rolesRes.data.roles || []);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (userId, currentStatus) => {
    try {
      await api.put(`/admin/users/${userId}/status`, {
        is_active: !currentStatus
      });
      loadData();
    } catch (error) {
      console.error('Failed to update user status:', error);
    }
  };

  const columns = [
    {
      key: 'name',
      label: 'User',
      render: (_, row) => (
        <div className="user-cell">
          <div className="user-avatar">
            {row.first_name?.[0]}{row.last_name?.[0]}
          </div>
          <div>
            <div className="user-name">{row.first_name} {row.last_name}</div>
            <div className="user-email">{row.email}</div>
          </div>
        </div>
      )
    },
    {
      key: 'username',
      label: 'Username'
    },
    {
      key: 'role_name',
      label: 'Role'
    },
    {
      key: 'business_name',
      label: 'Business',
      render: (value) => value || 'System'
    },
    {
      key: 'is_active',
      label: 'Status',
      render: (value) => (
        <span className={`status-badge ${value ? 'active' : 'inactive'}`}>
          {value ? 'Active' : 'Inactive'}
        </span>
      )
    },
    {
      key: 'last_login',
      label: 'Last Login',
      render: (value) => value ? formatDate(value) : 'Never'
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_, row) => (
        <div className="action-buttons">
          <Button 
            size="sm" 
            variant="secondary"
            onClick={() => {
              setEditingUser(row);
              setShowModal(true);
            }}
          >
            Edit
          </Button>
          <Button 
            size="sm" 
            variant={row.is_active ? 'warning' : 'success'}
            onClick={() => handleToggleStatus(row.id, row.is_active)}
          >
            {row.is_active ? 'Suspend' : 'Activate'}
          </Button>
        </div>
      )
    }
  ];

  return (
    <div className="system-users">
      <div className="page-header">
        <h1>System Users</h1>
        <Button 
          variant="primary" 
          onClick={() => {
            setEditingUser(null);
            setShowModal(true);
          }}
          icon="➕"
        >
          Add User
        </Button>
      </div>

      <Card>
        <Table 
          columns={columns}
          data={users}
          loading={loading}
        />
      </Card>

      <UserModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        user={editingUser}
        businesses={businesses}
        roles={roles}
        onSave={() => {
          setShowModal(false);
          loadData();
        }}
      />
    </div>
  );
};

const UserModal = ({ isOpen, onClose, user, businesses, roles, onSave }) => {
  const [formData, setFormData] = useState({
    username: user?.username || '',
    email: user?.email || '',
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    phone: user?.phone || '',
    role_id: user?.role_id || '',
    business_id: user?.business_id || '',
    password: '',
    confirm_password: ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user && formData.password !== formData.confirm_password) {
      alert('Passwords do not match');
      return;
    }

    setSaving(true);
    try {
      const data = { ...formData };
      if (!user) {
        // New user requires password
        if (!data.password) {
          alert('Password is required for new users');
          setSaving(false);
          return;
        }
      } else {
        // Editing user - remove password if empty
        if (!data.password) {
          delete data.password;
        }
      }
      
      delete data.confirm_password;

      if (user) {
        await api.put(`/admin/users/${user.id}`, data);
      } else {
        await api.post('/admin/users', data);
      }
      onSave();
    } catch (error) {
      console.error('Failed to save user:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={user ? 'Edit User' : 'New User'} size="lg">
      <form onSubmit={handleSubmit} className="user-form">
        <div className="form-row">
          <Input
            label="Username"
            value={formData.username}
            onChange={(e) => setFormData({...formData, username: e.target.value})}
            required
          />
          <Input
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({...formData, email: e.target.value})}
            required
          />
        </div>

        <div className="form-row">
          <Input
            label="First Name"
            value={formData.first_name}
            onChange={(e) => setFormData({...formData, first_name: e.target.value})}
            required
          />
          <Input
            label="Last Name"
            value={formData.last_name}
            onChange={(e) => setFormData({...formData, last_name: e.target.value})}
            required
          />
        </div>

        <div className="form-row">
          <Input
            label="Phone"
            value={formData.phone}
            onChange={(e) => setFormData({...formData, phone: e.target.value})}
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Role</label>
            <select
              value={formData.role_id}
              onChange={(e) => setFormData({...formData, role_id: e.target.value})}
              required
            >
              <option value="">Select Role</option>
              {roles.map(role => (
                <option key={role.id} value={role.id}>
                  {role.role_name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Business</label>
            <select
              value={formData.business_id}
              onChange={(e) => setFormData({...formData, business_id: e.target.value})}
            >
              <option value="">System-wide (No Business)</option>
              {businesses.map(business => (
                <option key={business.id} value={business.id}>
                  {business.business_name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {!user && (
          <>
            <div className="form-row">
              <Input
                label="Password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                required={!user}
                hint="Minimum 8 characters"
              />
              <Input
                label="Confirm Password"
                type="password"
                value={formData.confirm_password}
                onChange={(e) => setFormData({...formData, confirm_password: e.target.value})}
                required={!user}
              />
            </div>
          </>
        )}

        {user && (
          <div className="form-row">
            <Input
              label="New Password (leave blank to keep current)"
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({...formData, password: e.target.value})}
            />
          </div>
        )}

        <div className="form-actions">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" loading={saving}>
            {user ? 'Update User' : 'Create User'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default SystemUsers;